import {
    Box,
    Text,
    VStack,
    HStack,
    Flex,
    Collapse,
    useDisclosure,
    Icon,
    Tooltip,
} from '@chakra-ui/react';
import { TbChevronDown, TbChevronRight, TbFlag } from 'react-icons/tb';
import {
    buildReconciliationDiagram,
    type MatchReason,
} from '../utils/reconciliationDiagramData';
import type { InvoicePart } from '../hooks/invoice/useGetInvoiceDetail';
import { getNexLineDisplayQty, type NexLineQuantityBasis } from '../utils/nexLineQuantity';

const fmtUsd = (n: number): string =>
    n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

function LineCard({
    line,
    label,
    bg,
    displayDescription,
}: {
    line: InvoicePart;
    label?: string;
    bg?: string;
    /** When set, show this instead of line.title (e.g. invoice as-extracted). */
    displayDescription?: string;
}) {
    const description = displayDescription ?? line.title ?? '—';
    return (
        <Box
            bg={bg ?? 'white'}
            borderWidth="1px"
            borderColor="gray.200"
            borderRadius="md"
            p={3}
            fontSize="sm"
        >
            {label && (
                <Text fontSize="xs" color="gray.500" fontWeight="bold" mb={1}>
                    {label}
                </Text>
            )}
            <Text color="gray.900" fontWeight="medium" noOfLines={2}>
                {description}
            </Text>
            <HStack mt={2} spacing={3} flexWrap="wrap">
                <Text color="gray.600" fontFamily="mono" fontSize="xs">
                    {line.partNumber || '—'}
                </Text>
                <Text color="gray.500" fontSize="xs">
                    Qty {line.qty ?? 0}
                </Text>
                <Text color="gray.500" fontSize="xs">
                    ${fmtUsd(line.totalBuyPrice ?? 0)}
                </Text>
            </HStack>
        </Box>
    );
}

/** NEX API line: partDesc, partNumber, qtyOnOrder/qtyRec, extendedCost, etc. */
function NexLineCard({
    nexLine,
    label,
    invoiceType,
    nexLineQuantityBasis,
}: {
    nexLine: any;
    label?: string;
    invoiceType?: 'received' | 'returned' | 'return_slip' | 'sublet';
    nexLineQuantityBasis?: NexLineQuantityBasis;
}) {
    const desc = nexLine?.partDesc ?? nexLine?.description ?? '—';
    const partNumber = nexLine?.partNumber ?? '—';
    const qty = getNexLineDisplayQty(nexLine, invoiceType, nexLineQuantityBasis);
    const total = nexLine?.extendedCost ?? nexLine?.extended_cost ?? 0;
    return (
        <Box
            bg="blue.50"
            borderWidth="1px"
            borderColor="blue.200"
            borderRadius="md"
            p={3}
            fontSize="sm"
        >
            {label && (
                <Text fontSize="xs" color="blue.600" fontWeight="bold" mb={1}>
                    {label}
                </Text>
            )}
            <Text color="gray.900" fontWeight="medium" noOfLines={2}>
                {desc}
            </Text>
            <HStack mt={2} spacing={3} flexWrap="wrap">
                <Text color="gray.600" fontFamily="mono" fontSize="xs">
                    {partNumber}
                </Text>
                <Text color="gray.500" fontSize="xs">
                    Qty {qty}
                </Text>
                <Text color="gray.500" fontSize="xs">
                    ${fmtUsd(total)}
                </Text>
            </HStack>
        </Box>
    );
}

function ArrowWithReason({ reason }: { reason: MatchReason }) {
    return (
        <Tooltip label={reason} placement="top" hasArrow>
            <Flex direction="column" align="center" justify="center" minW="100px" px={2} flexShrink={0}>
                <Text fontSize="2xl" color="gray.400" aria-hidden>→</Text>
                <Text fontSize="xs" color="gray.600" textAlign="center" noOfLines={2} mt={1}>
                    {reason}
                </Text>
            </Flex>
        </Tooltip>
    );
}

export interface ReconciliationDiagramProps {
    lineItems: InvoicePart[];
    originalExtractedLineItems?: InvoicePart[];
    lineItemCorrectionMetadata?: Array<{
        type: 'expanded_to_match_nex' | 'consolidated_to_match_nex' | 'normalized_to_match_nex' | 'part_number_auto_corrected' | 'supersession_auto_corrected';
        partNumber: string;
        newIndices: number[];
        originalLine?: InvoicePart;
        originalLines?: InvoicePart[];
        newLines?: InvoicePart[];
        newLine?: InvoicePart;
        originalPartNumber?: string;
        matchedNexPartNumber?: string;
    }>;
    reconciliationIssues?: {
        flaggedParts?: Array<{ partIndex: number }>;
        probableMatches?: Array<{ partIndex: number; invoicePart: InvoicePart; nexLine: any }>;
    };
    invoiceType?: 'received' | 'returned' | 'return_slip' | 'sublet';
    nexLineQuantityBasis?: NexLineQuantityBasis;
}

export default function ReconciliationDiagram({
    lineItems,
    originalExtractedLineItems,
    lineItemCorrectionMetadata,
    reconciliationIssues,
    invoiceType,
    nexLineQuantityBasis = 'qtyRec',
}: ReconciliationDiagramProps) {
    const { groups, unusedNexLines } = buildReconciliationDiagram(
        lineItems,
        originalExtractedLineItems,
        lineItemCorrectionMetadata,
        reconciliationIssues
    );
    const { isOpen: isUnusedOpen, onToggle: onUnusedToggle } = useDisclosure({ defaultIsOpen: false });

    if (groups.length === 0) {
        return (
            <Box py={10} textAlign="center">
                <Text color="gray.500">No line items to display in the diagram.</Text>
            </Box>
        );
    }

    return (
        <VStack spacing={6} align="stretch">
            <HStack spacing={2} mb={2} flexWrap="wrap">
                <Text fontSize="xs" fontWeight="bold" color="gray.500" w="28%">
                    Invoice (as on invoice)
                </Text>
                <Box w="100px" flexShrink={0} />
                <Text fontSize="xs" fontWeight="bold" color="blue.600" w="28%">
                    NEX (matched)
                </Text>
                <Box w="100px" flexShrink={0} />
                <Text fontSize="xs" fontWeight="bold" color="gray.500" flex="1">
                    Final (reconciled)
                </Text>
            </HStack>

            {groups.map((group, idx) => (
                <Flex
                    key={idx}
                    align="stretch"
                    gap={2}
                    p={3}
                    borderRadius="lg"
                    borderWidth="1px"
                    borderColor={group.isFlagged ? 'orange.300' : 'gray.200'}
                    bg={group.isFlagged ? 'orange.50' : 'gray.50'}
                >
                    {group.isFlagged && (
                        <Tooltip label="This group has reconciliation flags" placement="top" hasArrow>
                            <Box alignSelf="center" flexShrink={0}>
                                <Icon as={TbFlag as React.ElementType} color="orange.500" boxSize={4} />
                            </Box>
                        </Tooltip>
                    )}
                    {/* Column 1: Invoice as extracted */}
                    <VStack flex="1" minW={0} spacing={2} align="stretch" w="28%">
                        {group.invoiceLines.map((line, i) => (
                            <LineCard
                                key={i}
                                line={line}
                                label={
                                    group.invoiceLines.length > 1
                                        ? `Invoice line ${i + 1}`
                                        : undefined
                                }
                                displayDescription={group.invoiceDescriptions?.[i]}
                            />
                        ))}
                    </VStack>
                    {/* Arrow + reason: Invoice → NEX */}
                    <ArrowWithReason reason={group.connectors[0]?.reason ?? 'Part number matches'} />
                    {/* Column 2: NEX matched line(s) */}
                    <VStack flex="1" minW={0} spacing={2} align="stretch" w="28%">
                        {group.nexLines.length > 0 ? (
                            group.nexLines.map((nl, i) => (
                                <NexLineCard
                                    key={i}
                                    nexLine={nl}
                                    invoiceType={invoiceType}
                                    nexLineQuantityBasis={nexLineQuantityBasis}
                                    label={
                                        group.nexLines.length > 1
                                            ? `NEX line ${i + 1}`
                                            : undefined
                                    }
                                />
                            ))
                        ) : (
                            <Box
                                bg="blue.50"
                                borderWidth="1px"
                                borderColor="blue.200"
                                borderStyle="dashed"
                                borderRadius="md"
                                p={3}
                                fontSize="sm"
                                color="gray.500"
                            >
                                <Text noOfLines={1}>No NEX line data</Text>
                            </Box>
                        )}
                    </VStack>
                    {/* Arrow: NEX → Final */}
                    <Flex direction="column" align="center" justify="center" minW="80px" px={1} flexShrink={0}>
                        <Text fontSize="2xl" color="gray.400" aria-hidden>→</Text>
                        <Text fontSize="xs" color="gray.500" textAlign="center" mt={1}>
                            Reconciled
                        </Text>
                    </Flex>
                    {/* Column 3: Final reconciled line item(s) */}
                    <VStack flex="1" minW={0} spacing={2} align="stretch">
                        {group.finalLines.map((line, i) => (
                            <LineCard
                                key={i}
                                line={line}
                                label={
                                    group.finalLines.length > 1
                                        ? `Final line ${i + 1}`
                                        : undefined
                                }
                                bg="white"
                            />
                        ))}
                    </VStack>
                </Flex>
            ))}

            {/* Unused NEX line items – collapsed by default */}
            <Box borderWidth="1px" borderColor="gray.200" borderRadius="lg" overflow="hidden">
                <Flex
                    align="center"
                    px={4}
                    py={3}
                    bg="gray.100"
                    cursor="pointer"
                    onClick={onUnusedToggle}
                    _hover={{ bg: 'gray.200' }}
                >
                    <Icon
                        as={(isUnusedOpen ? TbChevronDown : TbChevronRight) as React.ElementType}
                        boxSize={4}
                        color="gray.600"
                        mr={2}
                    />
                    <Text fontSize="sm" fontWeight="medium" color="gray.700">
                        Unused NEX line items
                        {unusedNexLines.length > 0 && (
                            <Text as="span" color="gray.500" fontWeight="normal" ml={2}>
                                ({unusedNexLines.length})
                            </Text>
                        )}
                    </Text>
                </Flex>
                <Collapse in={isUnusedOpen} animateOpacity>
                    <Box p={4} bg="white" borderTopWidth="1px" borderColor="gray.200">
                        {unusedNexLines.length === 0 ? (
                            <Text fontSize="sm" color="gray.500">
                                No unused NEX lines in the current reconciliation data. Full NEX line
                                list is not returned by the API; unused lines can be shown here once
                                the backend provides them.
                            </Text>
                        ) : (
                            <VStack align="stretch" spacing={2}>
                                {unusedNexLines.map((line: any, i: number) => (
                                    <LineCard key={i} line={line} bg="gray.50" />
                                ))}
                            </VStack>
                        )}
                    </Box>
                </Collapse>
            </Box>
        </VStack>
    );
}
